import { SeoLandingLayout, SeoLandingPageData } from '../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Logiciel de trésorerie entreprise : pilotez votre cash en temps réel | Trezoone',
    description: 'Logiciel de trésorerie pour TPE et PME : gestion des délais clients, prévision de cash, BFR, alertes. Trezoone connecté à votre banque dès le premier jour.',
    canonical: '/logiciel-tresorerie-entreprise',
    keywords: 'logiciel trésorerie entreprise, logiciel tresorerie pme, gestion tresorerie entreprise, outil tresorerie tpe, cash flow entreprise',
  },
  hero: {
    tag: 'Logiciel trésorerie entreprise',
    title: 'Vous avez des clients qui paient à 60 jours. Vos charges, elles, n\'attendent pas.',
    subtitle: 'Le décalage entre facturation et encaissement, c\'est le problème n°1 de trésorerie des TPE et PME. Trezoone vous aide à le mesurer, l\'anticiper et l\'absorber.',
    ctaLabel: 'Tester Trezoone',
  },
  pilierLink: {
    label: 'Guide complet : gestion trésorerie PME',
    href: '/gestion-tresorerie-pme',
  },
  sections: [
    {
      type: 'hook',
      title: 'Le paradoxe de l\'entreprise rentable à court de cash',
      content: 'C\'est le cas le plus fréquent : votre activité est bonne, votre carnet de commandes est plein. Et pourtant, vous êtes en tension de trésorerie.',
      items: [
        'Vous avez 80 000 € de créances clients non encaissées',
        'Vous avez 40 000 € de charges à payer ce mois-ci',
        'Résultat : vous êtes "riche sur le papier" et pauvre en liquide',
        'C\'est le Besoin en Fonds de Roulement (BFR) — et un logiciel de trésorerie vous aide à le gérer',
      ],
    },
    {
      type: 'explain',
      title: 'Ce que doit faire votre logiciel de trésorerie entreprise',
      content: 'Les entreprises ont des besoins spécifiques que les outils généralistes ne couvrent pas bien :',
      items: [
        'Suivi des créances clients avec date d\'échéance et alerte en cas de retard',
        'Gestion des dettes fournisseurs et optimisation des délais de paiement',
        'Calcul et suivi du BFR en temps réel',
        'Prévision de cash sur 3, 6 et 12 mois intégrant les délais de paiement réels',
        'Simulation de scénarios (nouveau contrat, gros investissement, recrutement)',
        'Rapports pour les banques dans le cadre d\'une demande de crédit de trésorerie',
      ],
    },
    {
      type: 'example',
      title: 'Cas réel : une PME de BTP qui reprend le contrôle',
      content: 'Une PME de 15 personnes dans le BTP : CA 1,2 M€, délais clients 60-90 jours, avances chantier importantes.',
      items: [
        'Avant Trezoone : gestion "à vue", découverts fréquents en fin de mois, stress permanent',
        'Après import : identification de 3 clients qui paient systématiquement à 90 jours au lieu de 60',
        'Action : conditions de paiement renforcées + acompte exigé → réduction du BFR de 40 000 €',
        'Plan de trésorerie sur 12 mois présenté à la banque → ligne de crédit de 100 000 € obtenue',
        'Résultat : zéro découvert depuis 8 mois, capacité d\'investissement retrouvée',
      ],
    },
    {
      type: 'midcta',
      title: 'Une PME BTP a réduit son BFR de 40 000 € grâce à Trezoone — testez',
      content: '14 jours gratuits · Connexion bancaire · Plan trésorerie pour votre banque inclus',
    },
    {
      type: 'solution',
      title: 'Trezoone pour votre entreprise',
      content: 'Un outil de trésorerie conçu pour les réalités du terrain : délais clients, TVA, charges sociales, saisonnalité.',
      items: [
        'Connexion bancaire automatique ou import CSV/OFX',
        'Suivi des créances et dettes avec indicateurs de retard',
        'Calcul du BFR et alerte en cas de dégradation',
        'Plan de trésorerie 12 mois pour vos rendez-vous banque',
        'Simulation de l\'impact d\'un nouveau contrat sur votre cash',
        'Tableaux de bord pour dirigeant (synthèse) et DAF (détail)',
        'Module PME avec suivi des factures et dépenses fixes',
      ],
    },
    {
      type: 'faq',
      title: 'Questions fréquentes',
      faq: [
        {
          q: 'Trezoone remplace-t-il notre ERP ou logiciel comptable ?',
          a: 'Non. Trezoone est complémentaire. Il se concentre sur la trésorerie opérationnelle (cash disponible, tensions futures) pendant que votre ERP/comptable gère la dimension fiscale et réglementaire.',
        },
        {
          q: 'Peut-on connecter Trezoone à notre logiciel de facturation ?',
          a: 'Les intégrations directes avec des logiciels de facturation sont en cours de développement. Actuellement, l\'import des données se fait via les relevés bancaires (CSV, OFX, PDF).',
        },
        {
          q: 'Trezoone est-il adapté aux entreprises saisonnières ?',
          a: 'Oui. Trezoone permet de modéliser des cycles saisonniers et de projeter leur impact sur la trésorerie. Particulièrement utile pour l\'hôtellerie, la restauration, le tourisme, le BTP.',
        },
      ],
    },
    {
      type: 'cluster',
      title: 'Trésorerie entreprise : PME, TPE et cas concrets',
      links: [
        { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-pme' },
        { label: 'Gestion trésorerie TPE', href: '/gestion-tresorerie-tpe' },
        { label: 'Logiciel de trésorerie', href: '/logiciel-tresorerie' },
        { label: 'Plan trésorerie PME', href: '/plan-tresorerie-pme' },
        { label: 'BFR trésorerie', href: '/bfr-tresorerie' },
        { label: 'Trésorerie startup', href: '/tresorerie-startup' },
        { label: 'Trésorerie freelance', href: '/tresorerie-freelance-independant' },
        { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
        { label: 'Problème cash flow', href: '/probleme-cash-flow' },
      ],
    },
    {
      type: 'cta',
      title: 'Reprenez le contrôle de votre cash',
      content: 'Testez Trezoone 14 jours gratuitement. Importez vos données en 5 minutes.',
    },
  ],
  relatedLinks: [
    { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-pme' },
    { label: 'Logiciel trésorerie', href: '/logiciel-tresorerie' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Tarifs', href: '/tarifs' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function LogicielTresorerieEntreprisePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
